import { WAD } from '@/lib/launchpad/constants'
import { parseEthToWei, weiToEthString } from '@/lib/launchpad/wei'
import {
  DEFAULT_MAX_CONTRIBUTION_ETH,
  DEFAULT_MIN_CONTRIBUTION_ETH,
  PRESALE_DEFAULT_DURATION_DAYS,
  PRESALE_DEFAULT_HARD_CAP_ETH,
  PRESALE_DEFAULT_SOFT_CAP_ETH,
  PRESALE_DEFAULT_TOKENS_PER_ETH,
  PRESALE_MAX_DURATION_DAYS,
  PRESALE_START_BUFFER_SECONDS,
  clampPresaleLpPct,
  creatorPctFromLp,
} from '@/lib/protocol'
import type { CreateSimPresaleInput, LivePresaleStatus, SimPresale } from '@/lib/presale/types'
import { trimOptionalUrl } from '@/lib/tokenSocials'
import { assertCreatorAllowed, assertPenguinPadBrandAllowed } from '@/lib/createGuard'

export const DEFAULT_TOKENS_PER_ETH = String(PRESALE_DEFAULT_TOKENS_PER_ETH)
export const PRESALE_MAX_START_DELAY_SECONDS = 7 * 24 * 60 * 60
export const PRESALE_MAX_START_DELAY_HOURS = 7 * 24

export function estimatePresaleSupplyWei(tokensPerEthWei: bigint, hardCapWei: bigint): bigint {
  if (tokensPerEthWei <= 0n || hardCapWei <= 0n)
    return 0n
  return (tokensPerEthWei * hardCapWei) / WAD
}

/** Tokens per ETH = total supply ÷ hard cap. Listing rate uses the same derived rate. */
export function presaleRateFromSupplyWei(totalSupplyWei: bigint, hardCapWei: bigint): bigint {
  if (totalSupplyWei <= 0n || hardCapWei <= 0n)
    return 0n
  return (totalSupplyWei * WAD) / hardCapWei
}

export function presaleWalletKey(address: string | undefined): string {
  return address?.trim().toLowerCase() ?? ''
}

export function isPresaleFinalized(presale: SimPresale): boolean {
  return Boolean(presale.finalized)
}

/** Launched only after a successful on-chain finalize — hard cap fill does not auto-launch. */
export function isPresaleLaunched(presale: SimPresale): boolean {
  if (presale.cancelled)
    return false
  if (isPresaleFinalized(presale) && presale.success === false)
    return false
  return isPresaleFinalized(presale) && presale.success !== false
}

export function getPresaleStatus(presale: SimPresale, nowMs = Date.now()): LivePresaleStatus {
  if (presale.cancelled)
    return 'failed'
  if (presale.success === false && isPresaleFinalized(presale))
    return 'failed'
  if (isPresaleLaunched(presale))
    return 'success'
  const startsAt = new Date(presale.startsAt).getTime()
  const endsAt = new Date(presale.endsAt).getTime()
  if (nowMs < startsAt)
    return 'upcoming'
  if (nowMs < endsAt)
    return 'live'
  return presale.raisedWei >= presale.softCapWei ? 'success' : 'failed'
}

export function remainingToHardCap(presale: SimPresale): bigint {
  return presale.hardCapWei > presale.raisedWei ? presale.hardCapWei - presale.raisedWei : 0n
}

export function isPresaleSoldOut(presale: SimPresale): boolean {
  return presale.hardCapWei > 0n && remainingToHardCap(presale) === 0n
}

export function pledgedByWallet(presale: SimPresale, address?: string): bigint {
  const key = presaleWalletKey(address)
  if (!key)
    return 0n
  return presale.pledges[key] ?? 0n
}

export function effectiveMaxWei(presale: SimPresale, address?: string): bigint {
  const pledged = pledgedByWallet(presale, address)
  const walletRoom = presale.maxContributionWei > pledged ? presale.maxContributionWei - pledged : 0n
  const capRoom = remainingToHardCap(presale)
  return walletRoom < capRoom ? walletRoom : capRoom
}

export function canContribute(presale: SimPresale, nowMs = Date.now()): boolean {
  return !presale.cancelled && getPresaleStatus(presale, nowMs) === 'live' && remainingToHardCap(presale) > 0n
}

export function canWithdraw(presale: SimPresale, address?: string, nowMs = Date.now()): boolean {
  if (pledgedByWallet(presale, address) <= 0n)
    return false
  if (presale.cancelled)
    return true
  const status = getPresaleStatus(presale, nowMs)
  return status === 'upcoming' || status === 'live'
}

export function canCancelPresale(presale: SimPresale, address?: string, nowMs = Date.now()): boolean {
  if (presale.cancelled)
    return false
  if (!address?.trim() || !presale.creatorAddress)
    return false
  if (address.trim().toLowerCase() !== presale.creatorAddress.trim().toLowerCase())
    return false
  const status = getPresaleStatus(presale, nowMs)
  if (status === 'upcoming')
    return true
  if (status !== 'live')
    return false
  const remainingMs = new Date(presale.endsAt).getTime() - nowMs
  return remainingMs > 24 * 60 * 60 * 1000
}

/** Manual finalize: after the end date if soft cap was met, or immediately when the hard cap is filled. Hard cap fill does not auto-launch. */
export function canFinalizePresale(presale: SimPresale, _address?: string, nowMs = Date.now()): boolean {
  if (presale.cancelled || isPresaleFinalized(presale))
    return false
  if (isPresaleSoldOut(presale) && presale.raisedWei >= presale.softCapWei)
    return true
  if (nowMs < new Date(presale.endsAt).getTime())
    return false
  return presale.raisedWei >= presale.softCapWei
}

export function validateContribution(presale: SimPresale, address: string, amountWei: bigint, nowMs = Date.now()): string | null {
  if (!address.trim())
    return 'Connect a wallet to contribute.'
  if (amountWei <= 0n)
    return 'Enter an amount greater than 0.'
  if (!canContribute(presale, nowMs))
    return remainingToHardCap(presale) === 0n
      ? 'This raise is at hard cap.'
      : 'Contributions are only open while the presale is live.'
  const pledged = pledgedByWallet(presale, address)
  const next = pledged + amountWei
  if (next > presale.maxContributionWei)
    return 'This amount would exceed the per-wallet maximum.'
  const remaining = remainingToHardCap(presale)
  if (amountWei > remaining)
    return 'This amount would exceed the hard cap.'
  const fillsHardCap = remaining > 0n && amountWei === remaining
  if (!fillsHardCap && presale.minContributionWei > 0n && amountWei < presale.minContributionWei)
    return `Contribution must be at least ${weiToEthString(presale.minContributionWei, 18)} ETH.`
  return null
}

export function validateWithdrawal(presale: SimPresale, address: string, amountWei: bigint, nowMs = Date.now()): string | null {
  if (!address.trim())
    return 'Connect a wallet to withdraw.'
  if (amountWei <= 0n)
    return 'Enter an amount greater than 0.'
  const status = getPresaleStatus(presale, nowMs)
  if (!presale.cancelled && status !== 'upcoming' && status !== 'live')
    return 'Withdrawals are only open before the presale closes, or after the creator cancels.'
  const pledged = pledgedByWallet(presale, address)
  if (amountWei > pledged)
    return 'You cannot withdraw more than your pledged ETH.'
  return null
}

export function parsePresaleCreateInput(raw: {
  name: string
  ticker: string
  category: CreateSimPresaleInput['category']
  tagline: string
  description: string
  imageUrl?: string
  website?: string
  twitter?: string
  youtube?: string
  telegram?: string
  discord?: string
  tiktok?: string
  facebook?: string
  instagram?: string
  creatorAddress: string
  lpPct: number
  teamPct?: number
  softCapEth: string
  hardCapEth: string
  minEth: string
  maxEth: string
  durationDays?: string
  tokensPerEth?: string
  listingRate?: string
  startDelayHours?: string
  startsAtLocal?: string
  endsAtLocal?: string
  goLiveNow?: boolean
  totalSupply?: string
}): CreateSimPresaleInput {
  const name = raw.name.trim()
  const ticker = raw.ticker.trim().toUpperCase()
  const creatorAddress = raw.creatorAddress.trim()
  const softCapWei = parseEthToWei(raw.softCapEth)
  const hardCapWei = parseEthToWei(raw.hardCapEth)
  const minContributionWei = parseEthToWei(raw.minEth || String(DEFAULT_MIN_CONTRIBUTION_ETH))
  const maxContributionWei = parseEthToWei(raw.maxEth || String(DEFAULT_MAX_CONTRIBUTION_ETH))
  const totalSupplyWei = parseEthToWei(raw.totalSupply || '0')
  const tokensPerEthWei = totalSupplyWei > 0n && hardCapWei > 0n
    ? presaleRateFromSupplyWei(totalSupplyWei, hardCapWei)
    : parseEthToWei(raw.tokensPerEth || DEFAULT_TOKENS_PER_ETH)
  const listingRateWei = tokensPerEthWei
  const window = parsePresaleSchedule(raw.startsAtLocal, raw.endsAtLocal, raw.durationDays, raw.startDelayHours, Date.now(), raw.goLiveNow)
  const lpPct = clampPresaleLpPct(raw.lpPct)
  const teamPct = creatorPctFromLp(lpPct)

  if (!name || !ticker)
    throw new Error('Project name and ticker are required.')
  if (name.length > 64)
    throw new Error('Name must be 64 characters or fewer.')
  if (ticker.length > 12)
    throw new Error('Ticker must be 12 characters or fewer.')
  const imageUrl = raw.imageUrl?.trim() || undefined
  if (imageUrl && imageUrl.length > 512)
    throw new Error('Token image URL is too long for the factory (512 characters max).')
  if (!creatorAddress)
    throw new Error('Connect a wallet to open this presale.')
  assertCreatorAllowed(creatorAddress)
  assertPenguinPadBrandAllowed(name, ticker, creatorAddress)
  if (softCapWei <= 0n || hardCapWei <= 0n)
    throw new Error('Soft cap and hard cap must be greater than 0 ETH.')
  if (hardCapWei < softCapWei)
    throw new Error('Hard cap must be greater than or equal to soft cap.')
  if (hardCapWei > softCapWei * 4n)
    throw new Error('Hard cap cannot be more than 4× the soft cap.')
  if (minContributionWei <= 0n || maxContributionWei <= 0n)
    throw new Error('Per-wallet min and max must be greater than 0 ETH.')
  if (maxContributionWei < minContributionWei)
    throw new Error('Per-wallet max must be at least the per-wallet min.')
  if (maxContributionWei > hardCapWei)
    throw new Error('Per-wallet max cannot exceed the hard cap.')
  if (minContributionWei > hardCapWei)
    throw new Error('Per-wallet min cannot exceed the hard cap.')
  if (totalSupplyWei <= 0n)
    throw new Error('Token supply must be greater than 0.')
  if (tokensPerEthWei <= 0n)
    throw new Error('Token supply must be greater than 0 so tokens per ETH can be derived from supply ÷ hard cap.')

  return {
    name,
    ticker,
    category: raw.category,
    tagline: raw.tagline.trim(),
    description: raw.description.trim(),
    imageUrl,
    website: trimOptionalUrl(raw.website),
    twitter: trimOptionalUrl(raw.twitter),
    youtube: trimOptionalUrl(raw.youtube),
    telegram: trimOptionalUrl(raw.telegram),
    discord: trimOptionalUrl(raw.discord),
    tiktok: trimOptionalUrl(raw.tiktok),
    facebook: trimOptionalUrl(raw.facebook),
    instagram: trimOptionalUrl(raw.instagram),
    creatorAddress,
    lpPct,
    softCapWei,
    hardCapWei,
    minContributionWei,
    maxContributionWei,
    tokensPerEthWei,
    listingRateWei,
    teamPct,
    startDelaySeconds: window.startDelaySeconds,
    totalSupplyWei,
    durationDays: window.durationDays,
    startsAtUnix: window.startsAtUnix,
    endsAtUnix: window.endsAtUnix,
  }
}

export function toDatetimeLocalValue(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export function parseDatetimeLocalToUnix(value: string, label: string): bigint {
  const trimmed = value.trim()
  if (!trimmed)
    throw new Error(`${label} is required.`)
  const ms = new Date(trimmed).getTime()
  if (!Number.isFinite(ms))
    throw new Error(`${label} is not a valid date and time.`)
  return BigInt(Math.floor(ms / 1000))
}

export function parsePresaleSchedule(
  startsAtLocal?: string,
  endsAtLocal?: string,
  durationDaysRaw?: string,
  startDelayHoursRaw?: string,
  nowMs = Date.now(),
  goLiveNow = false,
) {
  const nowUnix = BigInt(Math.floor(nowMs / 1000))
  const earliestStart = nowUnix + BigInt(PRESALE_START_BUFFER_SECONDS)
  const defaultLength = BigInt(PRESALE_DEFAULT_DURATION_DAYS * 24 * 60 * 60)

  if (goLiveNow) {
    let windowLength = defaultLength
    if (endsAtLocal?.trim()) {
      const pickedEnd = parseDatetimeLocalToUnix(endsAtLocal, 'End date')
      const pickedStart = startsAtLocal?.trim()
        ? parseDatetimeLocalToUnix(startsAtLocal, 'Start date')
        : earliestStart
      windowLength = pickedEnd > pickedStart ? pickedEnd - pickedStart : defaultLength
    }
    const startsAtUnix = earliestStart
    const endsAtUnix = startsAtUnix + windowLength
    if (endsAtUnix <= startsAtUnix)
      throw new Error('End date must be after the start date.')
    const durationSeconds = Number(windowLength)
    if (durationSeconds > PRESALE_MAX_DURATION_DAYS * 24 * 60 * 60)
      throw new Error(`Presale window cannot be longer than ${PRESALE_MAX_DURATION_DAYS} days.`)
    return {
      startsAtUnix,
      endsAtUnix,
      durationDays: Math.max(1, Math.min(PRESALE_MAX_DURATION_DAYS, Math.ceil(durationSeconds / (24 * 60 * 60)))),
      startDelaySeconds: PRESALE_START_BUFFER_SECONDS,
    }
  }

  if (startsAtLocal?.trim() && endsAtLocal?.trim()) {
    const pickedStart = parseDatetimeLocalToUnix(startsAtLocal, 'Start date')
    const pickedEnd = parseDatetimeLocalToUnix(endsAtLocal, 'End date')
    const startsAtUnix = pickedStart < earliestStart ? earliestStart : pickedStart
    const pickedLength = pickedEnd > pickedStart ? pickedEnd - pickedStart : 0n
    const endsAtUnix = startsAtUnix + (pickedLength > 0n ? pickedLength : defaultLength)
    if (endsAtUnix <= startsAtUnix)
      throw new Error('End date must be after the start date.')
    const durationSeconds = Number(endsAtUnix - startsAtUnix)
    const durationDays = Math.max(1, Math.min(PRESALE_MAX_DURATION_DAYS, Math.ceil(durationSeconds / (24 * 60 * 60))))
    if (durationSeconds > PRESALE_MAX_DURATION_DAYS * 24 * 60 * 60)
      throw new Error(`Presale window cannot be longer than ${PRESALE_MAX_DURATION_DAYS} days.`)
    return {
      startsAtUnix,
      endsAtUnix,
      durationDays,
      startDelaySeconds: Number(startsAtUnix > nowUnix ? startsAtUnix - nowUnix : 0n),
    }
  }

  const durationDays = Math.max(1, Math.min(PRESALE_MAX_DURATION_DAYS, Math.round(Number(durationDaysRaw) || PRESALE_DEFAULT_DURATION_DAYS)))
  const startDelayHours = Math.max(0, Math.min(PRESALE_MAX_START_DELAY_HOURS, Math.round(Number(startDelayHoursRaw) || 0)))
  const startDelaySeconds = Math.max(PRESALE_START_BUFFER_SECONDS, startDelayHours * 60 * 60)
  const startsAtUnix = nowUnix + BigInt(startDelaySeconds)
  const endsAtUnix = startsAtUnix + BigInt(durationDays * 24 * 60 * 60)
  return { startsAtUnix, endsAtUnix, durationDays, startDelaySeconds }
}

/** Production create-form caps. Restore by switching defaultPresaleCreateValues() back to these. */
export const PRESALE_CREATE_CAPS_PRODUCTION = {
  softCapEth: String(PRESALE_DEFAULT_SOFT_CAP_ETH),
  hardCapEth: String(PRESALE_DEFAULT_HARD_CAP_ETH),
  minEth: String(DEFAULT_MIN_CONTRIBUTION_ETH),
  maxEth: String(DEFAULT_MAX_CONTRIBUTION_ETH),
} as const

/** Temporary cheap test caps. Hard cap stays at 4× soft cap; per-wallet max cannot exceed hard cap. */
export const PRESALE_CREATE_CAPS_TEST = {
  softCapEth: '0.01',
  hardCapEth: '0.04',
  minEth: '0.001',
  maxEth: '0.04',
} as const

export function defaultPresaleCreateValues(nowMs = Date.now()) {
  const start = new Date(nowMs + PRESALE_START_BUFFER_SECONDS * 1000)
  const end = new Date(start.getTime() + PRESALE_DEFAULT_DURATION_DAYS * 24 * 60 * 60 * 1000)
  // Live release: production caps (soft 5 / hard 20 / min 0.01 / max 10 ETH). Live factory minSoftCap() can still raise the floor.
  const caps = PRESALE_CREATE_CAPS_PRODUCTION
  return {
    ...caps,
    durationDays: String(PRESALE_DEFAULT_DURATION_DAYS),
    totalSupply: String(PRESALE_DEFAULT_TOKENS_PER_ETH * PRESALE_DEFAULT_HARD_CAP_ETH),
    startDelayHours: '0',
    startsAtLocal: toDatetimeLocalValue(start),
    endsAtLocal: toDatetimeLocalValue(end),
  }
}
